import { useCallback, useMemo, useState } from 'react';

import { useProductionChat } from '@/hooks/useProductionChat';
import { apiFetch } from '@/lib/api';
import { normKey } from '@/lib/assetKeys';
import { getJson, setQueryData, useQuery } from '@/lib/query';

export interface Candidate {
  index: number;
  path: string;
  score: number;
  approved: boolean;
}

export interface GateAsset {
  id: string;
  type?: string;
  path?: string;
  /** Voice gate: who speaks this line. */
  speaker?: string;
  /** Voice-policy gate: the scripted line itself. */
  text?: string;
  /** Blocked-shot gate: why the shot can't be generated as written. */
  reason?: string;
  candidates?: Candidate[];
  chosen_index?: number;
}

export interface Gate {
  phase: string;
  assets: GateAsset[];
  /**
   * Monotonic gate revision, bumped every time the gate re-opens after a rework.
   * Appended to image URLs as `?rev=N` so the browser refetches the freshly reworked
   * frame — the engine caches full-res assets for an hour and ignores the query
   * string, so a changed URL is the only cache-bust that works.
   */
  revision?: number;
  /**
   * True once reworks stop converging on the producer's note. The gate becomes a HARD
   * HOLD: the engine will not auto-proceed past it, and only an explicit approval, a
   * candidate pick, or a new direction releases it.
   */
  stalled?: boolean;
}

/** A confirmed Director proposal whose target matches an asset in the open gate. */
export interface Flag {
  assetId: string;
  note: string;
}

export interface ResolvePayload {
  rejectedIds: string[];
  feedback: Record<string, string>;
  selections?: Record<string, number>;
}

/** Chat proposal types that count as a queued rework against a gate asset. */
const REWORK_TYPES = new Set(['rework_keyframe', 'reshoot_clip', 'redesign_character', 'redesign_location']);

// A gate can open at any step; 10s keeps the wait short without hammering the API
// (this used to poll every 4s, plus /api/chat each time, and requests piled up).
const POLL_MS = 10_000;

interface ReviewData {
  waiting?: boolean;
  phase?: string;
  assets?: GateAsset[];
  revision?: number;
  stalled?: boolean;
}

const reviewKey = (productionId: string) => `review:${productionId}`;

/**
 * Drives the human-in-the-loop review gate.
 *
 * While a gate is open, confirmed Instruct/Fix proposals accumulate as FLAGS.
 * Resolving with the batch sends them all as rejections in ONE round (one
 * regeneration pass, one re-review) rather than the old per-flag
 * consume → regenerate → re-open cycle.
 */
export function useReviewGate(productionId: string, running: boolean) {
  const [busy, setBusy] = useState(false);

  // Cached + shared (lib/query): reopening the production shows the gate at once.
  const review = useQuery<ReviewData>(
    reviewKey(productionId),
    () => getJson<ReviewData>(() => apiFetch(`/api/review/${productionId}`)),
    { intervalMs: running ? POLL_MS : undefined },
  );
  const refetchReview = review.refetch;
  const gate: Gate | null = useMemo(() => {
    const d = review.data;
    return d?.waiting && d.phase ? { phase: d.phase, assets: d.assets || [], revision: d.revision, stalled: d.stalled } : null;
  }, [review.data]);

  // Rework flags only matter while a gate is open — read the shared chat then.
  const chat = useProductionChat(gate ? productionId : null, running);

  const flags: Flag[] = useMemo(() => {
    if (!gate) return [];
    // Match a proposal's free-text target to a gate asset id, exactly first then
    // by containment (the Director may say "Research Commons" for RESEARCH_COMMONS).
    const byNorm = new Map(gate.assets.map((a) => [normKey(a.id), a.id]));
    const found: Flag[] = [];
    const seen = new Set<string>();
    for (const m of chat.data?.messages || []) {
      if (m.kind !== 'proposal' || m.status !== 'confirmed') continue;
      const action = m.action || {};
      if (!REWORK_TYPES.has(String(action.type || ''))) continue;
      // A frame change isn't a re-shoot of the same frame: at the clips gate it stays
      // queued and the engine applies it right after (new frame, then a new take).
      if (gate.phase === 'clips' && action.type === 'rework_keyframe') continue;
      const tgt = normKey(String(action.target || ''));
      let assetId = byNorm.get(tgt);
      if (!assetId && tgt) {
        for (const [k, v] of byNorm) {
          if (k.includes(tgt) || tgt.includes(k)) {
            assetId = v;
            break;
          }
        }
      }
      if (assetId && !seen.has(assetId)) {
        seen.add(assetId);
        // Not translated: this note is sent BACK to the engine as rework feedback
        // for an agent to act on, so it must stay in the engine's language.
        found.push({ assetId, note: String(action.note || 'Producer flagged for rework') });
      }
    }
    return found;
  }, [gate, chat.data]);

  /** Resolve the open gate. Anything not rejected is approved. */
  const resolve = useCallback(
    async ({ rejectedIds, feedback, selections }: ResolvePayload) => {
      if (!gate || busy) return;
      setBusy(true);
      try {
        const rejectedSet = new Set(rejectedIds);
        await apiFetch(`/api/approve/${productionId}/${gate.phase}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            approved_ids: gate.assets.map((a) => a.id).filter((id) => !rejectedSet.has(id)),
            rejected_ids: rejectedIds,
            feedback,
            selections: selections ?? {},
          }),
        });
        // Close the gate locally at once, then confirm with the server.
        setQueryData<ReviewData>(reviewKey(productionId), () => ({ waiting: false }));
        refetchReview().catch(() => {});
      } finally {
        setBusy(false);
      }
    },
    [gate, busy, productionId, refetchReview],
  );

  return { gate, flags, busy, resolve };
}
